import { useControllableState, mergeRefs } from '@doppio/core';
import { IconSearch } from '@doppio/icons';
import { View } from 'dripsy';
import * as React from 'react';
import { useFocus } from 'react-native-web-hooks';
import { FormControl } from '../FormControl';
import { Input } from '../Input';
import { AutocompleteList } from './AutocompleteList';
import { AutocompletePortal } from './AutocompletePortal';
const TEST_ID = 'doppio-autocomplete';
const ITEM_HEIGHT = 32;
const MAX_VISIBLE_ITEMS = 7;
const Autocomplete = React.forwardRef(({ id, label, helperText, labelTooltip, defaultValue, isInvalid, isRequired, isDisabled, options = [], onSelect, onScroll, onFocus, onBlur, ...props }, forwardedRef) => {
    const localRef = React.useRef(null);
    const listRef = React.useRef(null);
    const isFocusVisible = useFocus(localRef);
    const [hoveredItem, setHoveredItem] = React.useState(null);
    const [isPortalOpen, setPortalOpen] = React.useState(false);
    const [isSuggestionListOpen, setSuggestionListOpen] = React.useState(false);
    const [value, setValue] = useControllableState({
        value: props.value,
        defaultValue,
        onChange: props.onChangeText,
    });
    const filteredOptions = React.useMemo(() => options.filter((e) => e.label.toLowerCase().includes(value?.toLowerCase())), [value, options]);
    const listHeight = React.useMemo(() => filteredOptions.length < MAX_VISIBLE_ITEMS
        ? filteredOptions.length * ITEM_HEIGHT
        : MAX_VISIBLE_ITEMS * ITEM_HEIGHT, [filteredOptions]);
    const handleTextChange = (text) => {
        setValue(text);
        if (!isSuggestionListOpen) {
            setSuggestionListOpen(true);
        }
        if (filteredOptions.length > 0) {
            setHoveredItem(0);
        }
    };
    const handleItemSelect = React.useCallback((option) => {
        if (onSelect)
            onSelect(option);
        setValue(option.label);
        setSuggestionListOpen(false);
    }, []);
    const handleKeyPress = (event) => {
        switch (event.nativeEvent.key) {
            case 'ArrowUp': {
                event.preventDefault();
                if (isSuggestionListOpen && filteredOptions.length > 0) {
                    const nextIndex = (hoveredItem || 0) === 0 ? filteredOptions.length - 1 : (hoveredItem || 0) - 1;
                    setHoveredItem(nextIndex);
                    listRef.current?.scrollToItem(nextIndex);
                }
                break;
            }
            case 'ArrowDown': {
                event.preventDefault();
                if (isSuggestionListOpen && filteredOptions.length > 0) {
                    const nextIndex = hoveredItem === null ? 0 : ((hoveredItem || 0) + 1) % filteredOptions.length;
                    setHoveredItem(nextIndex);
                    listRef.current?.scrollToItem(nextIndex);
                }
                break;
            }
            case 'Enter': {
                event.preventDefault();
                if (hoveredItem !== null && isSuggestionListOpen && filteredOptions.length > 0) {
                    if (onSelect)
                        onSelect(filteredOptions[hoveredItem]);
                    setValue(filteredOptions[hoveredItem].label);
                    setSuggestionListOpen(false);
                }
                break;
            }
            case 'Escape': {
                event.preventDefault();
                setValue('');
                setSuggestionListOpen(false);
            }
        }
    };
    return (React.createElement(FormControl.Group, { formElementId: id, label: label, labelTooltip: labelTooltip, helperText: helperText, isDisabled: isDisabled, isRequired: isRequired, isInvalid: isInvalid }, ({ helperTextId }) => (React.createElement(View, { focusable: false, accessibilityRole: "combobox", "aria-haspopup": "listbox", "aria-owns": isPortalOpen ? `${id}-items` : null, "aria-controls": id, "aria-expanded": isSuggestionListOpen },
        React.createElement(Input, { id: id, ...props, autoComplete: "off", ref: mergeRefs([localRef, forwardedRef]), helperTextId: helperTextId, value: value, isDisabled: isDisabled, isRequired: isRequired, isInvalid: isInvalid, sx: {
                textOverflow: 'ellipsis',
                ...((isFocusVisible || isInvalid) && { borderColor: 'backgroundMedium' }),
            }, onFocus: (event) => {
                if (onFocus)
                    onFocus(event);
                event.preventDefault();
                setPortalOpen(true);
            }, onBlur: (event) => {
                if (onBlur)
                    onBlur(event);
                setSuggestionListOpen(false);
                setPortalOpen(false);
                setHoveredItem(null);
            }, iconLeft: React.createElement(View, { sx: { height: '$5', width: '$5', marginLeft: '$1' } },
                React.createElement(IconSearch, { color: isDisabled ? 'secondaryBackgroundActive' : 'text' })), onChangeText: handleTextChange, onKeyPress: handleKeyPress, "aria-autocomplete": "list", "aria-controls": isPortalOpen ? `${id}-items` : null, "aria-activedescendant": hoveredItem !== -1 ? `${id}-item-${hoveredItem}` : null }),
        isPortalOpen && (React.createElement(AutocompletePortal, { referenceElement: localRef.current, isInvalid: isInvalid, isSuggestionListOpen: isSuggestionListOpen },
            React.createElement(AutocompleteList, { autocompleteId: id, listHeight: listHeight, itemHeight: ITEM_HEIGHT, keyword: value, options: filteredOptions, ref: listRef, hoveredItem: hoveredItem, onItemSelect: handleItemSelect, onItemHover: setHoveredItem, onScroll: onScroll })))))));
});
const AutocompleteCompound = Object.assign({}, Autocomplete, {
    displayName: 'Autocomplete',
    testID: TEST_ID,
});
export { AutocompleteCompound as Autocomplete };
//# sourceMappingURL=Autocomplete.js.map